import React, {useState} from 'react';
import { Box } from '@mui/material';
import GridHeader from "./GridHeader";
import GridContent from "./GridContent";
import GridLegend from "./GridLegend";
import {useMutation, useQuery} from "@apollo/client";
import {GridDetail} from "../../graphql/queries/grid";
import _ from "lodash";
import {useNavigate, useParams} from "react-router-dom";
import {LoadingScreen} from "@dreebit-registry/vsm-microservice-components";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import {CreateGridItem} from "../../graphql/mutations/createGridItem";
import {DeleteGrid} from "../../graphql/mutations/deleteGrid";
import {toast} from "react-toastify";
import {useTranslation} from "react-i18next";
import ConfirmDeleteModal from "../ConfirmDeleteModal";

interface Props {
    gridViewId: string
}

const GridView = ({gridViewId}: Props) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {  gridId } = useParams();

    const gridDetailsQuery = useQuery(GridDetail, {
        variables: {
            id: gridId
        }
    })
    const gridDetails = _.get(gridDetailsQuery, 'data.grid')
    const azimuth = _.get(gridDetails, 'azimuth', 0)

    const [isEditMode, setIsEditMode] = useState(false)
    const [createGridItemMutation] = useMutation(CreateGridItem)
    const [deleteGridMutation, deleteGridResult] = useMutation(DeleteGrid)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

    const handleAddDoor = () => {
        createGridItemMutation({
            variables: {
                gridId: gridId
            },
            refetchQueries: ["GridDetail"]
        })
    }

    const handleDeleteGrid = () => {
        deleteGridMutation({
            variables: {
                id: gridId
            },
            refetchQueries: ["GridList"]
        })
            .then((res) => {
                setDeleteConfirmOpen(false)
                if (res.data?.deleteGrid?.success && res.data?.deleteGrid?.message) {
                    toast.success(res.data.deleteGrid.message);
                    navigate(`/gridViews/${gridViewId}`)
                } else {
                    toast.error(t("Error deleting grid."))
                }
            })
            .catch((err) => {
                setDeleteConfirmOpen(false)
                toast.error(resolveErrorMessage(err, t))
            })
    }

    if (gridDetailsQuery.loading) {
        return <LoadingScreen size={"lg"}/>
    }

    return (
        <Box display="flex" height="100%" width="100%">
            <Box flex={1} display="flex" flexDirection="column">
                <GridHeader
                    isEditMode={isEditMode}
                    onToggleEdit={() => setIsEditMode(prev => !prev)}
                    onAddDoor={handleAddDoor}
                    onDeleteGrid={() => setDeleteConfirmOpen(true)}
                />
                <GridContent
                    gridViewId={gridViewId}
                    isEditMode={isEditMode}
                    grid={gridDetails}
                />
            </Box>
            <GridLegend azimuth={azimuth} />
            {deleteConfirmOpen && (
                <ConfirmDeleteModal
                    open={deleteConfirmOpen}
                    title={t('Delete grid')}
                    message={t('Are you sure you want to delete the grid {{name}} including all of its doors? This action cannot be undone.', {name: _.get(gridDetails, 'title')})}
                    loading={deleteGridResult.loading}
                    onConfirm={handleDeleteGrid}
                    onClose={() => setDeleteConfirmOpen(false)}
                />
            )}
        </Box>
    );
}

export default GridView;
